import { useRef, useState, type JSX } from 'react';
import { reencodeToJpeg } from './reencodeToJpeg';

export interface CapturedImage {
  /** `data:...;base64,…` — ready to POST or preview. */
  dataUrl: string;
  fileName: string;
  mimeType: string;
}

/**
 * Take a receipt photo (rear camera) or pick one from the library, and get back a
 * compressed data URL. The key job is **client-side downscale + compress**: a phone
 * photo is 3–12MB (HEIC on iPhone) and would otherwise be rejected by a size cap or
 * a payload-clamped API — this shrinks it to a few hundred KB before it leaves the
 * device. PDFs pass straight through. Part of the CCAANZ mobile standard; styling
 * rides the shared `.btn`/`.cluster`/`.help`/`.error` classes.
 */
export function ImageCapture({
  value,
  onChange,
  onRemove,
  maxEdge = 1600,
  quality = 0.82,
  disabled,
  label = 'photo',
}: {
  value?: CapturedImage | null;
  onChange: (img: CapturedImage) => void;
  onRemove?: () => void;
  /** Longest edge of the compressed image, px. */
  maxEdge?: number;
  /** JPEG quality 0–1. */
  quality?: number;
  disabled?: boolean;
  label?: string;
}): JSX.Element {
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = (file: File | undefined): void => {
    if (!file) return;
    setError(null);
    setBusy(true);
    processFile(file, maxEdge, quality)
      .then(onChange)
      .catch(() =>
        setError("That image couldn't be read. Try taking a photo, or choose a JPEG or PNG."),
      )
      .finally(() => {
        setBusy(false);
        // reset so re-selecting the same file fires change again
        if (cameraRef.current) cameraRef.current.value = '';
        if (libraryRef.current) libraryRef.current.value = '';
      });
  };

  const isImage = !!value && value.mimeType.startsWith('image/');

  return (
    <div>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => handle(e.target.files?.[0])}
      />
      <input
        ref={libraryRef}
        type="file"
        accept="image/*,application/pdf"
        hidden
        onChange={(e) => handle(e.target.files?.[0])}
      />

      {value ? (
        <div>
          {isImage ? (
            <img
              src={value.dataUrl}
              alt={label}
              style={{
                width: '100%',
                maxWidth: 240,
                maxHeight: 170,
                objectFit: 'cover',
                borderRadius: 'var(--radius-sm, 8px)',
                border: '1px solid var(--color-border, #e5e0d5)',
                display: 'block',
                marginBottom: 8,
              }}
            />
          ) : (
            <p style={{ marginBottom: 8, fontSize: 13.5 }}>📄 {value.fileName}</p>
          )}
          <div className="cluster">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={disabled || busy}
              onClick={() => libraryRef.current?.click()}
            >
              Replace
            </button>
            {onRemove && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={disabled || busy}
                onClick={onRemove}
              >
                Remove
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="cluster">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={disabled || busy}
            onClick={() => cameraRef.current?.click()}
          >
            Take photo
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={disabled || busy}
            onClick={() => libraryRef.current?.click()}
          >
            Choose from library
          </button>
        </div>
      )}
      {busy && <p className="help">Preparing…</p>}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/** Downscale+compress an image to a JPEG data URL under `maxEdge`, through the
 *  shared canvas re-encode (reencodeToJpeg); pass non-images (PDF) through
 *  unchanged — this component takes PDFs, so it decides that before the helper,
 *  which refuses them. Throws if the browser can't decode the image. */
async function processFile(file: File, maxEdge: number, quality: number): Promise<CapturedImage> {
  if (!file.type.startsWith('image/')) {
    return { dataUrl: await readAsDataUrl(file), fileName: file.name, mimeType: file.type };
  }
  const result = await reencodeToJpeg(file, { maxEdge, quality });
  if (!result.ok) throw new Error(result.reason);
  const base = file.name.replace(/\.[^.]+$/, '') || 'photo';
  return { dataUrl: result.dataUrl, fileName: `${base}.jpg`, mimeType: 'image/jpeg' };
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('read-failed'));
    reader.readAsDataURL(file);
  });
}
